import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import axios from "axios";
import { CheckCircle, MessageSquare, Palette, Target, ArrowRight, Download, FolderOpen, Loader2 } from "lucide-react";
import { Toaster, toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer, FUNDING } from "@paypal/react-paypal-js";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Email Form Component
const EmailForm = ({ variant = "primary" }) => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }
    
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      navigate('/thank-you');
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      setIsSubmitting(false);
    }
  };

  const buttonText = "Send Me the Toolkit";
  const reassurance = variant === "final" 
    ? "Free. Immediate. Designed to move you forward today."
    : "Instant download. No spam. Just clarity.";

  return (
    <form onSubmit={handleSubmit} className="w-full" data-testid={`email-form-${variant}`}>
      <div className="flex flex-col gap-3">
        <input
          type="email"
          placeholder="Enter your email for instant access"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
          className="w-full h-14 min-h-[56px] rounded-full border-2 border-[#E8E4DC] bg-white px-5 text-[16px] text-charcoal placeholder:text-warmgray/50 focus:outline-none focus:border-terracotta transition-colors disabled:opacity-50 touch-manipulation"
          style={{ fontSize: '16px' }}
          data-testid={`email-input-${variant}`}
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
        />
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-14 min-h-[56px] rounded-full bg-terracotta text-white font-semibold text-base hover:bg-[#B85B45] transition-colors shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap touch-manipulation active:scale-[0.98] relative z-10"
          data-testid={`submit-button-${variant}`}
        >
          {isSubmitting ? "Sending..." : buttonText}
        </button>
      </div>
      <p className="text-sm text-warmgray/70 mt-4 text-center">
        {reassurance}
      </p>
    </form>
  );
};

// Landing Page
const LandingPage = () => {
  const struggles = [
    "Your visuals keep changing",
    "You're unsure what to say",
    "Your offer feels unclear",
    "You hesitate to launch"
  ];

  const outcomes = [
    {
      icon: <MessageSquare className="w-6 h-6" strokeWidth={1.5} />,
      title: "Define Your Brand Message",
      description: "Know exactly how to describe what you do."
    },
    {
      icon: <Palette className="w-6 h-6" strokeWidth={1.5} />,
      title: "Clarify Your Visual Direction",
      description: "Choose colors, tone, and style with confidence."
    },
    {
      icon: <Target className="w-6 h-6" strokeWidth={1.5} />,
      title: "Focus Your Next Step",
      description: "Stop spinning. Start building."
    }
  ];

  return (
    <div className="min-h-screen bg-cream font-body overflow-x-hidden">
      <Toaster position="top-center" richColors />
      
      {/* ===== HERO SECTION ===== */}
      <section className="py-12 md:py-24 px-4 md:px-8 lg:px-12 overflow-hidden" data-testid="hero-section">
        <div className="w-full max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Micro Label */}
              <p 
                className="text-xs tracking-[0.2em] uppercase font-semibold text-sage mb-5 animate-fade-up"
                data-testid="hero-label"
              >
                Free Brand Starter
              </p>
              
              {/* Main Headline */}
              <h1 
                className="font-heading text-3xl sm:text-4xl lg:text-[3.25rem] font-bold tracking-tight leading-[1.15] text-charcoal mb-5 animate-fade-up"
                data-testid="hero-headline"
              >
                Build Your Brand With Direction — Not Guesswork
              </h1>
              
              {/* Subheadline */}
              <p 
                className="text-base md:text-xl leading-relaxed text-warmgray mb-5 animate-fade-up"
                data-testid="hero-subheadline"
              >
                Download the Brand Foundation Toolkit and get clear on your message, visuals, and next steps before you start posting.
              </p>
              
              {/* Support Copy */}
              <div className="mb-6 animate-fade-up">
                <p className="text-sm md:text-base text-warmgray/80">No more changing your name.</p>
                <p className="text-sm md:text-base text-warmgray/80">No more second-guessing your brand.</p>
              </div>
              
              {/* Email Form */}
              <div className="w-full lg:max-w-md animate-fade-up relative z-20">
                <EmailForm variant="primary" />
              </div>
            </div>
            
            {/* Right Image */}
            <div className="hidden lg:flex justify-center lg:justify-end animate-fade-up">
              <div className="relative">
                <div className="absolute -z-10 top-6 -right-4 w-full h-full bg-sage/20 rounded-3xl"></div>
                <img
                  src="https://images.unsplash.com/photo-1636485042715-f0e844b82338?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxmZW1pbmluZSUyMHdvcmtzcGFjZSUyMGRlc2slMjBmbGF0bGF5JTIwc3RhdGlvbmVyeSUyMHBhc3RlbCUyMGJyaWdodHxlbnwwfHx8fDE3NzExMTc0NTV8MA&ixlib=rb-4.1.0&q=85"
                  alt="Feminine workspace with soft pastel aesthetic"
                  className="w-full max-w-md rounded-3xl shadow-xl object-cover aspect-[4/5]"
                  data-testid="hero-image"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PROBLEM IDENTIFICATION SECTION ===== */}
      <section className="py-16 md:py-28 px-4 md:px-8 bg-white/40 overflow-hidden" data-testid="problem-section">
        <div className="w-full max-w-2xl mx-auto">
          <h2 
            className="font-heading text-2xl sm:text-3xl md:text-4xl font-semibold text-charcoal text-center mb-6 md:mb-8"
            data-testid="problem-headline"
          >
            If You're Feeling Stuck, It's Not a Talent Problem
          </h2>
          
          <p className="text-base md:text-lg text-warmgray text-center mb-8 md:mb-10">
            Most women don't struggle with ideas — they struggle with direction.
          </p>
          
          <div className="flex flex-col items-center gap-3 md:gap-4 mb-8 md:mb-10">
            {struggles.map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-3"
              >
                <div className="w-2 h-2 rounded-full bg-terracotta/60 flex-shrink-0"></div>
                <span className="text-sm md:text-base text-warmgray">{item}</span>
              </div>
            ))}
          </div>
          
          <p 
            className="text-lg md:text-xl font-heading font-semibold text-charcoal text-center mb-3"
          >
            Direction removes doubt.
          </p>
          
          <p 
            className="text-lg md:text-xl font-heading font-semibold text-charcoal text-center"
            data-testid="problem-closing"
          >
            Clarity changes everything.
          </p>
        </div>
      </section>

      {/* ===== WHAT YOU'LL WALK AWAY WITH ===== */}
      <section className="py-16 md:py-28 px-4 md:px-8 overflow-hidden" data-testid="outcomes-section">
        <div className="w-full max-w-3xl mx-auto">
          <h2 
            className="font-heading text-2xl sm:text-3xl md:text-4xl font-semibold text-charcoal text-center mb-10 md:mb-14"
            data-testid="outcomes-headline"
          >
            In 15 Minutes, You'll Have:
          </h2>
          
          <div className="space-y-4 md:space-y-5">
            {outcomes.map((item, index) => (
              <div
                key={index}
                className="bg-white p-5 md:p-7 rounded-2xl border border-[#E8E4DC]/60 shadow-sm"
                data-testid={`outcome-card-${index}`}
              >
                <div className="flex items-start gap-4 md:gap-5">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-sage/15 flex items-center justify-center flex-shrink-0 text-sage">
                    {item.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-heading text-lg md:text-xl font-semibold text-charcoal mb-1">
                      {item.title}
                    </h3>
                    <p className="text-sm md:text-base text-warmgray">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== POSITIONING SECTION ===== */}
      <section className="py-16 md:py-24 px-4 md:px-8 bg-sand/40 overflow-hidden" data-testid="positioning-section">
        <div className="w-full max-w-2xl mx-auto text-center">
          <h2 
            className="font-heading text-xl sm:text-2xl md:text-3xl font-semibold text-charcoal mb-5 md:mb-6"
            data-testid="positioning-headline"
          >
            Built by Bloomwright Studio
          </h2>
          
          <p className="text-sm md:text-lg text-warmgray leading-relaxed mb-6 md:mb-8 px-2">
            Bloomwright creates AI-powered brand foundations for women building thoughtful brands. Designed to help you begin with structure — whether you're starting fresh or refining what already exists.
          </p>
          
          <a
            href="#"
            className="inline-flex items-center gap-2 text-sm font-medium text-sage hover:text-charcoal transition-colors touch-manipulation min-h-[44px] px-4"
            data-testid="explore-studio-link"
          >
            Explore Bloomwright Studio
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* ===== FINAL CTA SECTION ===== */}
      <section className="py-16 md:py-28 px-4 md:px-8 overflow-hidden" data-testid="final-cta-section">
        <div className="w-full max-w-2xl mx-auto">
          <h2 
            className="font-heading text-2xl sm:text-3xl md:text-4xl font-semibold text-charcoal text-center mb-8 md:mb-10"
            data-testid="final-cta-headline"
          >
            Start With Clarity
          </h2>
          
          <div className="w-full max-w-lg mx-auto relative z-20">
            <EmailForm variant="final" />
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="py-8 md:py-10 px-4 md:px-8 border-t border-[#E8E4DC]">
        <div className="w-full max-w-2xl mx-auto text-center">
          <p className="font-heading text-base md:text-lg font-semibold text-charcoal mb-1">
            Bloomwright
          </p>
          <p className="text-xs md:text-sm text-warmgray">
            Clarity before visibility.
          </p>
        </div>
      </footer>
    </div>
  );
};

// Thank You / Download Page
const ThankYouPage = () => {
  const toolkitUrl = "/Brand-Foundation-Toolkit.html";

  return (
    <div className="min-h-screen bg-cream font-body flex items-center justify-center py-16 px-5">
      <Toaster position="top-center" richColors />
      
      <div className="w-full max-w-xl mx-auto text-center">
        {/* Success Icon */}
        <div className="w-20 h-20 rounded-full bg-sage/20 flex items-center justify-center mx-auto mb-8 animate-fade-up">
          <CheckCircle className="w-10 h-10 text-sage" strokeWidth={1.5} />
        </div>
        
        {/* Label */}
        <p 
          className="text-xs tracking-[0.2em] uppercase font-semibold text-sage mb-4 animate-fade-up"
          data-testid="thankyou-label"
        >
          You're In
        </p>
        
        {/* Headline */}
        <h1 
          className="font-heading text-3xl sm:text-4xl font-bold tracking-tight leading-[1.1] text-charcoal mb-6 animate-fade-up"
          data-testid="thankyou-headline"
        >
          Your Brand Foundation Toolkit is ready
        </h1>
        
        {/* Subheadline */}
        <p 
          className="text-lg leading-relaxed text-warmgray mb-10 animate-fade-up"
          data-testid="thankyou-subheadline"
        >
          Here's your toolkit — created to give you direction fast.
        </p>
        
        {/* Download Button */}
        <div className="animate-fade-up">
          <a
            href={toolkitUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 h-16 px-10 rounded-full bg-terracotta text-white font-semibold text-lg hover:bg-[#B85B45] transition-colors shadow-md hover:shadow-lg"
            data-testid="download-button"
          >
            <Download className="w-5 h-5" />
            Download the Toolkit (PDF)
          </a>
          
          <p className="text-sm text-warmgray/70 mt-4">
            Opens in a new tab. Use <strong>Print → Save as PDF</strong> to save.
          </p>
        </div>
        
        {/* How to Save */}
        <div className="mt-10 p-6 bg-sand/50 rounded-2xl animate-fade-up">
          <div className="flex items-center justify-center gap-2 text-warmgray mb-2">
            <FolderOpen className="w-5 h-5" />
            <span className="font-medium">How to save it</span>
          </div>
          <p className="text-sm text-warmgray/80">
            Press <strong>Ctrl+P</strong> (or <strong>Cmd+P</strong> on Mac) → Select "Save as PDF"
          </p>
        </div>
        
        {/* Next Step */}
        <p className="text-sm text-sage font-medium mt-8 animate-fade-up">
          Start with Step 1: the Trust-Ready Brand Checklist.
        </p>
        
        {/* Back Link */}
        <div className="mt-12 animate-fade-up">
          <a 
            href="/"
            className="text-sm text-warmgray/60 hover:text-terracotta transition-colors underline underline-offset-4"
            data-testid="back-home-link"
          >
            ← Back to Bloomwright
          </a>
        </div>
      </div>
    </div>
  );
};

// $47 Brand Blueprint Sales Page
const BlueprintSalesPage = () => {
  const forYouList = [
    "You've done the thinking but need structure",
    "You're starting something new and want direction",
    "You're refining an existing brand",
    "You want clarity without hiring a strategist"
  ];

  const deliverables = [
    "A structured brand positioning statement",
    "Refined messaging you can immediately use",
    "Visual tone and aesthetic guidance",
    "A clear starting direction for execution"
  ];

  return (
    <div className="min-h-screen bg-cream font-body overflow-x-hidden">
      <Toaster position="top-center" richColors />
      
      {/* ===== HERO SECTION ===== */}
      <section className="py-16 md:py-24 px-4 md:px-8" data-testid="blueprint-hero">
        <div className="w-full max-w-2xl mx-auto text-center">
          <h1 
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] text-charcoal mb-6"
            data-testid="blueprint-headline"
          >
            Generate Your Personalized Brand Blueprint
          </h1>
          
          <p className="text-base md:text-lg text-warmgray mb-4 leading-relaxed">
            Answer a few guided questions and receive a structured brand foundation you can confidently build from.
          </p>
          
          <p className="text-sm text-warmgray/80 mb-10">
            No guessing. No scattered notes. Just cohesive direction.
          </p>
          
          <a
            href="/checkout"
            className="inline-block w-full sm:w-auto h-14 px-8 rounded-full bg-terracotta text-white font-semibold text-base hover:bg-[#B85B45] transition-colors shadow-md hover:shadow-lg leading-[3.5rem] touch-manipulation"
            data-testid="blueprint-cta-hero"
          >
            Generate My Brand Blueprint — $47
          </a>
          
          <p className="text-sm text-warmgray/60 mt-4">
            Instant access inside Bloomwright Studio.
          </p>
        </div>
      </section>

      {/* ===== WHAT THIS IS ===== */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-white/40" data-testid="blueprint-what">
        <div className="w-full max-w-2xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-charcoal text-center mb-6">
            What This Actually Does
          </h2>
          
          <p className="text-base text-warmgray text-center mb-8 leading-relaxed">
            Bloomwright takes your answers and refines them into a cohesive Brand Blueprint — organizing your positioning, messaging, and visual direction into one clear foundation.
          </p>
          
          <div className="bg-white rounded-2xl border border-[#E8E4DC]/60 p-6 md:p-8">
            <ul className="space-y-3">
              {deliverables.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-terracotta mt-1">•</span>
                  <span className="text-warmgray">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ===== WHO THIS IS FOR ===== */}
      <section className="py-16 md:py-20 px-4 md:px-8" data-testid="blueprint-who">
        <div className="w-full max-w-2xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-charcoal text-center mb-8">
            This Is For You If:
          </h2>
          
          <div className="flex flex-col items-center gap-3 mb-8">
            {forYouList.map((item, index) => (
              <div key={index} className="flex items-start gap-3">
                <span className="text-sage mt-1">•</span>
                <span className="text-warmgray">{item}</span>
              </div>
            ))}
          </div>
          
          <p className="text-lg font-heading font-semibold text-charcoal text-center">
            It's clarity — accelerated.
          </p>
        </div>
      </section>

      {/* ===== DIFFERENTIATION ===== */}
      <section className="py-16 md:py-20 px-4 md:px-8 bg-sand/40" data-testid="blueprint-diff">
        <div className="w-full max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-charcoal mb-6">
            Not Random AI Output
          </h2>
          
          <p className="text-base text-warmgray leading-relaxed">
            This blueprint is generated using Bloomwright's guided framework — designed specifically for women building thoughtful brands. Your responses shape the structure. The output is strategic, not generic.
          </p>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-16 md:py-24 px-4 md:px-8" data-testid="blueprint-final-cta">
        <div className="w-full max-w-2xl mx-auto text-center">
          <h2 className="font-heading text-xl sm:text-2xl md:text-3xl font-semibold text-charcoal mb-4">
            A Strategic Starting Point — Without Hiring a Brand Strategist
          </h2>
          
          <p className="text-base text-warmgray mb-8">
            You've done the thinking.<br />
            Now turn it into structured direction you can confidently execute.
          </p>
          
          <a
            href="/checkout"
            className="inline-block w-full sm:w-auto h-14 px-8 rounded-full bg-terracotta text-white font-semibold text-base hover:bg-[#B85B45] transition-colors shadow-md hover:shadow-lg leading-[3.5rem] touch-manipulation"
            data-testid="blueprint-cta-final"
          >
            Generate My Structured Brand Blueprint — $47
          </a>
          
          <p className="text-sm text-warmgray/60 mt-4">
            Instant access. Secure checkout. Delivered inside Bloomwright Studio.
          </p>
        </div>
      </section>

      {/* ===== FAQ SECTION ===== */}
      <section className="py-12 md:py-16 px-4 md:px-8 bg-white/40" data-testid="blueprint-faq">
        <div className="w-full max-w-2xl mx-auto">
          <h3 className="font-heading text-xl md:text-2xl font-semibold text-charcoal text-center mb-8">
            Before You Continue
          </h3>
          
          <div className="space-y-3">
            <details className="group bg-white rounded-xl border border-[#E8E4DC]/60 overflow-hidden">
              <summary className="flex justify-between items-center p-5 cursor-pointer list-none font-medium text-charcoal hover:text-terracotta transition-colors">
                <span>How long does this take?</span>
                <span className="text-sage group-open:rotate-45 transition-transform">+</span>
              </summary>
              <div className="px-5 pb-5 text-warmgray text-sm">
                About 10–15 minutes to answer the guided questions.
              </div>
            </details>
            
            <details className="group bg-white rounded-xl border border-[#E8E4DC]/60 overflow-hidden">
              <summary className="flex justify-between items-center p-5 cursor-pointer list-none font-medium text-charcoal hover:text-terracotta transition-colors">
                <span>When do I receive my Blueprint?</span>
                <span className="text-sage group-open:rotate-45 transition-transform">+</span>
              </summary>
              <div className="px-5 pb-5 text-warmgray text-sm">
                Immediately after submission — delivered inside Bloomwright Studio.
              </div>
            </details>
            
            <details className="group bg-white rounded-xl border border-[#E8E4DC]/60 overflow-hidden">
              <summary className="flex justify-between items-center p-5 cursor-pointer list-none font-medium text-charcoal hover:text-terracotta transition-colors">
                <span>Is this generic AI output?</span>
                <span className="text-sage group-open:rotate-45 transition-transform">+</span>
              </summary>
              <div className="px-5 pb-5 text-warmgray text-sm">
                No. Your responses shape the structure. Bloomwright uses a guided framework designed specifically for thoughtful brands.
              </div>
            </details>
            
            <details className="group bg-white rounded-xl border border-[#E8E4DC]/60 overflow-hidden">
              <summary className="flex justify-between items-center p-5 cursor-pointer list-none font-medium text-charcoal hover:text-terracotta transition-colors">
                <span>Can I use this for a new or existing brand?</span>
                <span className="text-sage group-open:rotate-45 transition-transform">+</span>
              </summary>
              <div className="px-5 pb-5 text-warmgray text-sm">
                Yes. It works whether you're starting fresh or refining what already exists.
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="py-8 px-4 md:px-8 border-t border-[#E8E4DC]">
        <div className="w-full max-w-2xl mx-auto text-center">
          <p className="font-heading text-base font-semibold text-charcoal mb-1">
            Bloomwright
          </p>
          <p className="text-xs text-warmgray">
            Clarity before visibility.
          </p>
        </div>
      </footer>
    </div>
  );
};

// Checkout Page
const CheckoutPage = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [testMode, setTestMode] = useState(false);
  const [showTestTab, setShowTestTab] = useState(false);
  const navigate = useNavigate();

  const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;

  // Check for test mode via URL parameter: ?test=true
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('test') === 'true') {
      setTestMode(true);
      setShowTestTab(true);
    }
  }, []);

  // Test Mode: Simulate successful payment
  const handleTestPayment = async () => {
    if (!email || !name) {
      toast.error("Please fill in your email and name");
      return;
    }
    
    setIsProcessing(true);
    toast.success("Processing test payment...");
    
    // Generate fake order ID
    const testOrderId = `TEST-${Date.now()}`;
    
    // Save order to backend
    try {
      await axios.post(`${API}/checkout/complete`, {
        email: email,
        name: name,
        paypal_order_id: testOrderId,
        payer_email: email,
        amount: "47.00",
        currency: "USD",
      });
      toast.success("Order saved to database!");
    } catch (err) {
      console.error("Failed to save order:", err);
      toast.error("Failed to save order, but continuing...");
    }
    
    // Show success
    setTimeout(() => {
      toast.success(`Test payment completed! Thank you, ${name.split(' ')[0]}!`);
      toast.success("Confirmation email sent!");
      toast.success("Redirecting to your Brand Blueprint...");
      
      // Redirect after short delay
      setTimeout(() => {
        window.location.href = `https://bloomwrightstudio.com/guided-creation?email=${encodeURIComponent(email)}&order_id=${testOrderId}`;
      }, 2000);
    }, 1500);
  };

  const handleCardCheckout = (e) => {
    e.preventDefault();
    if (!email || !name) {
      toast.error("Please fill in your email and name");
      return;
    }
    setIsProcessing(true);
    toast.success("Processing card payment...");
    // Simulate card payment - in production, this would integrate with Stripe
    setTimeout(() => {
      toast.success("Payment successful! Redirecting...");
      setIsProcessing(false);
    }, 2000);
  };

  const createPayPalOrder = (data, actions) => {
    if (!email || !name) {
      toast.error("Please fill in your email and name first");
      return Promise.reject(new Error("Missing customer info"));
    }
    return actions.order.create({
      intent: "CAPTURE",
      purchase_units: [
        {
          description: "Personalized Brand Blueprint",
          amount: {
            currency_code: "USD",
            value: "47.00",
          },
        },
      ],
      application_context: {
        shipping_preference: "NO_SHIPPING",
        user_action: "PAY_NOW",
        return_url: window.location.href,
        cancel_url: window.location.href,
      },
    });
  };

  const onPayPalApprove = async (data, actions) => {
    try {
      console.log("PayPal order approved, attempting capture...", data);
      const details = await actions.order.capture();
      console.log("Payment captured successfully:", details);
      toast.success(`Payment completed! Thank you, ${details.payer.name.given_name}!`);
      
      // Save order to backend
      try {
        await axios.post(`${API}/checkout/complete`, {
          email: email,
          name: name,
          paypal_order_id: details.id,
          payer_email: details.payer.email_address,
          amount: details.purchase_units[0].amount.value,
          currency: details.purchase_units[0].amount.currency_code,
        });
      } catch (err) {
        console.error("Failed to save order:", err);
        // Continue with redirect even if save fails
      }
      
      // Redirect to Bloomwright Studio
      toast.success("Redirecting to your Brand Blueprint...");
      setTimeout(() => {
        window.location.href = `https://bloomwrightstudio.com/?email=${encodeURIComponent(email)}&order=${details.id}`;
      }, 1500);
      
    } catch (err) {
      console.error("PayPal capture error:", err);
      console.error("Error details:", JSON.stringify(err, null, 2));
      
      // Show more specific error message
      let errorMessage = "Payment failed. Please try again.";
      if (err?.message) {
        errorMessage = `Payment failed: ${err.message}`;
      }
      if (err?.details?.[0]?.description) {
        errorMessage = `Payment failed: ${err.details[0].description}`;
      }
      toast.error(errorMessage);
    }
  };

  const onPayPalError = (err) => {
    console.error("PayPal error:", err);
    toast.error("Payment failed. Please try again.");
  };

  // PayPal Buttons component for PayPal payment method (PayPal + Pay Later only)
  const PayPalButtonsWrapper = ({ email, name }) => {
    const [{ isPending, isRejected }] = usePayPalScriptReducer();
    
    if (isPending) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-terracotta mb-3" />
          <p className="text-sm text-warmgray">Loading PayPal...</p>
        </div>
      );
    }
    
    if (isRejected) {
      return (
        <div className="text-center py-6 bg-red-50 rounded-lg border border-red-200">
          <p className="text-red-600 mb-3">PayPal could not be loaded.</p>
          <p className="text-sm text-warmgray mb-4">Please try paying with card instead, or refresh the page.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-terracotta font-medium hover:underline"
          >
            Refresh page
          </button>
        </div>
      );
    }
    
    return (
      <div className="paypal-buttons-paypal">
        {/* Primary PayPal Button */}
        <PayPalButtons
          fundingSource={FUNDING.PAYPAL}
          style={{
            layout: "vertical",
            color: "gold",
            shape: "pill",
            label: "pay",
            height: 55,
          }}
          createOrder={createPayPalOrder}
          onApprove={onPayPalApprove}
          onError={onPayPalError}
          disabled={!email || !name}
        />
        {/* Pay Later - smaller, less prominent */}
        <div className="pay-later-secondary mt-2">
          <PayPalButtons
            fundingSource={FUNDING.PAYLATER}
            style={{
              layout: "vertical",
              color: "silver",
              shape: "pill",
              label: "pay",
              height: 40,
            }}
            createOrder={createPayPalOrder}
            onApprove={onPayPalApprove}
            onError={onPayPalError}
            disabled={!email || !name}
          />
        </div>
      </div>
    );
  };

  // Card Buttons component for Card payment method (Debit/Credit only)
  const CardButtonsWrapper = ({ email, name }) => {
    const [{ isPending, isRejected }] = usePayPalScriptReducer();
    
    if (isPending) {
      return (
        <div className="flex flex-col items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-terracotta mb-3" />
          <p className="text-sm text-warmgray">Loading payment...</p>
        </div>
      );
    }
    
    if (isRejected) {
      return (
        <div className="text-center py-6 bg-red-50 rounded-lg border border-red-200">
          <p className="text-red-600 mb-3">Payment could not be loaded.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="text-terracotta font-medium hover:underline"
          >
            Refresh page
          </button>
        </div>
      );
    }
    
    return (
      <PayPalButtons
        fundingSource={FUNDING.CARD}
        style={{
          layout: "vertical",
          color: "black",
          shape: "pill",
          label: "pay",
          height: 55,
        }}
        createOrder={createPayPalOrder}
        onApprove={onPayPalApprove}
        onError={onPayPalError}
        disabled={!email || !name}
      />
    );
  };

  return (
    <PayPalScriptProvider 
      options={{ 
        clientId: PAYPAL_CLIENT_ID, 
        currency: "USD",
        intent: "capture",
        components: "buttons"
      }}
    >
      <div className="min-h-screen bg-cream font-body overflow-x-hidden">
        <Toaster position="top-center" richColors />
        
        <div className="py-12 md:py-20 px-4 md:px-8">
          <div className="w-full max-w-lg mx-auto">
            {/* Header */}
            <div className="text-center mb-10">
              <h1 
                className="font-heading text-2xl sm:text-3xl font-bold text-charcoal mb-4"
                data-testid="checkout-headline"
              >
                Complete Your Structured Brand Blueprint
              </h1>
              <p className="text-sm md:text-base text-warmgray leading-relaxed">
                This secure checkout gives you immediate access to your personalized Brand Blueprint inside Bloomwright Studio.
              </p>
              <p className="text-sm text-warmgray/80 mt-2">
                You'll answer a few guided questions — and receive your structured foundation instantly.
              </p>
            </div>
            
            {/* Checkout Card */}
            <div className="bg-white rounded-2xl border border-[#E8E4DC] shadow-sm p-6 md:p-8">
              {/* Product */}
              <div className="border-b border-[#E8E4DC] pb-6 mb-6">
                <h2 className="font-heading text-lg font-semibold text-charcoal mb-2">
                  Personalized Brand Blueprint
                </h2>
                <p className="text-sm text-warmgray">
                  Your structured brand foundation — positioning, messaging, and visual direction.
                </p>
              </div>
              
              {/* Price */}
              <div className="text-center mb-6">
                <p className="text-3xl font-bold text-charcoal" data-testid="checkout-price">$47</p>
                <p className="text-sm text-warmgray mt-1">One-Time Payment</p>
                <p className="text-xs text-warmgray/70">No subscription. No hidden fees.</p>
              </div>
              
              {/* Customer Info */}
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Email</label>
                  <input
                    type="email"
                    required
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-12 rounded-lg border-2 border-[#E8E4DC] bg-white px-4 text-[16px] text-charcoal placeholder:text-warmgray/50 focus:outline-none focus:border-terracotta transition-colors"
                    style={{ fontSize: '16px' }}
                    data-testid="checkout-email-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-charcoal mb-2">Name</label>
                  <input
                    type="text"
                    required
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full h-12 rounded-lg border-2 border-[#E8E4DC] bg-white px-4 text-[16px] text-charcoal placeholder:text-warmgray/50 focus:outline-none focus:border-terracotta transition-colors"
                    style={{ fontSize: '16px' }}
                    data-testid="checkout-name-input"
                  />
                </div>
              </div>
              
              {/* Payment Method Selection */}
              <div className="mb-6">
                <p className="text-sm text-warmgray mb-1 text-center">Select your payment method to continue.</p>
                <p className="text-xs text-warmgray/60 mb-3 text-center">You'll have instant access after payment.</p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('card')}
                    className={`flex-1 h-12 min-h-[48px] rounded-lg border-2 px-3 flex items-center justify-center gap-2 font-medium transition-colors touch-manipulation ${
                      paymentMethod === 'card' 
                        ? 'border-terracotta bg-terracotta/5 text-charcoal' 
                        : 'border-[#E8E4DC] bg-white text-warmgray hover:border-warmgray/50'
                    }`}
                    data-testid="payment-method-card"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <rect x="2" y="5" width="20" height="14" rx="2" strokeWidth="2"/>
                      <path d="M2 10h20" strokeWidth="2"/>
                    </svg>
                    Card
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('paypal')}
                    className={`flex-1 h-12 min-h-[48px] rounded-lg border-2 px-3 flex items-center justify-center gap-2 font-medium transition-colors touch-manipulation ${
                      paymentMethod === 'paypal' 
                        ? 'border-terracotta bg-terracotta/5 text-charcoal' 
                        : 'border-[#E8E4DC] bg-white text-warmgray hover:border-warmgray/50'
                    }`}
                    data-testid="payment-method-paypal"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.646h6.583c2.179 0 3.893.548 4.988 1.592 1.05 1.003 1.538 2.462 1.392 4.262-.019.231-.043.469-.076.715a7.353 7.353 0 0 1-.266 1.19c-.644 2.208-2.016 3.63-3.96 4.253-.862.277-1.82.418-2.85.418H9.477a.77.77 0 0 0-.758.646l-.734 4.634a.77.77 0 0 1-.758.646l-.151-.093z"/>
                    </svg>
                    PayPal
                  </button>

                  {/* Test Tab - Always visible for testing */}
                  <button
                    type="button"
                    onClick={() => {
                      setPaymentMethod('test');
                      setTestMode(true);
                      setShowTestTab(true);
                    }}
                    className={`flex-1 h-12 min-h-[48px] rounded-lg border-2 px-3 flex items-center justify-center gap-2 font-medium transition-colors touch-manipulation ${
                      paymentMethod === 'test' 
                        ? 'border-orange-400 bg-orange-50 text-orange-600' 
                        : 'border-[#E8E4DC] bg-white text-warmgray hover:border-orange-300'
                    }`}
                    data-testid="payment-method-test"
                  >
                    <span className="text-lg">🧪</span>
                    Test
                  </button>
                </div>
              </div>
              
              {/* Test Payment Section */}
              {paymentMethod === 'test' && (
                <div className="test-payment-container" data-testid="test-payment-container">
                  <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-5 mb-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-2xl">🧪</span>
                      <h3 className="font-semibold text-orange-700">Test Mode</h3>
                    </div>
                    <p className="text-sm text-orange-600 mb-4">
                      PayPal sandbox is currently down. Use this to simulate a successful payment and test the full flow.
                    </p>
                    <div className="bg-white rounded-lg p-4 mb-4 text-sm text-gray-600">
                      <p className="font-medium text-gray-700 mb-2">This will:</p>
                      <ul className="space-y-1">
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span> Save order to database
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span> Send confirmation email
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span> Redirect to BloomwrightStudio
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="text-green-500">✓</span> Grant full access
                        </li>
                      </ul>
                    </div>
                    {(!email || !name) ? (
                      <div className="text-center py-2 mb-3 bg-orange-100 rounded-lg px-4">
                        <p className="text-sm text-orange-700">Enter your email and name above first.</p>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={handleTestPayment}
                        disabled={isProcessing}
                        className="w-full h-14 rounded-full bg-orange-500 text-white font-semibold text-base hover:bg-orange-600 transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="test-payment-button"
                      >
                        {isProcessing ? (
                          <span className="flex items-center justify-center gap-2">
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                            </svg>
                            Processing...
                          </span>
                        ) : (
                          "🧪 Simulate Successful Payment"
                        )}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* Card Payment Section - Shows PayPal's Card button */}
              {paymentMethod === 'card' && (
                <div className="card-button-container" data-testid="card-buttons-container">
                  {(!email || !name) && (
                    <div className="text-center py-2 mb-3 bg-sage/10 rounded-lg border border-sage/30 px-4">
                      <p className="text-sm text-charcoal/70">Enter your email and name to unlock payment options.</p>
                    </div>
                  )}
                  {email && name && (
                    <p className="text-sm text-warmgray text-center mb-3">Complete your purchase securely with card.</p>
                  )}
                  <CardButtonsWrapper email={email} name={name} />
                </div>
              )}
              
              {/* PayPal Payment Section - Shows PayPal + Pay Later */}
              {paymentMethod === 'paypal' && (
                <div className="paypal-button-container" data-testid="paypal-buttons-container">
                  {(!email || !name) && (
                    <div className="text-center py-2 mb-3 bg-sage/10 rounded-lg border border-sage/30 px-4">
                      <p className="text-sm text-charcoal/70">Enter your email and name to unlock payment options.</p>
                    </div>
                  )}
                  {email && name && (
                    <p className="text-sm text-warmgray text-center mb-3">Complete your purchase securely with PayPal.</p>
                  )}
                  <PayPalButtonsWrapper email={email} name={name} />
                </div>
              )}
              
              {/* Trust Signals */}
              <div className="mt-6 pt-6 border-t border-[#E8E4DC]">
                <div className="flex flex-col items-center gap-2 text-sm text-warmgray/70">
                  <div className="flex items-center gap-2">
                    <span>🔒</span>
                    <span>Secure encrypted checkout</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>⚡</span>
                    <span>Instant access after purchase</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span>✨</span>
                    <span>Built for women building thoughtful brands</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Back Link */}
            <div className="text-center mt-8">
              <a 
                href="/blueprint"
                className="text-sm text-warmgray/60 hover:text-terracotta transition-colors"
                data-testid="back-to-blueprint-link"
              >
                ← Back to details
              </a>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <footer className="py-8 px-4 border-t border-[#E8E4DC]">
          <div className="text-center">
            <p className="font-heading text-base font-semibold text-charcoal mb-1">Bloomwright</p>
            <p className="text-xs text-warmgray">Clarity before visibility.</p>
          </div>
        </footer>
      </div>
    </PayPalScriptProvider>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/thank-you" element={<ThankYouPage />} />
          <Route path="/blueprint" element={<BlueprintSalesPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
